import { Box, Button, TextField, useMediaQuery, useTheme, CircularProgress, Typography } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useAddNewOrganizationMutation } from "../../../features/organizations/organizationsApiSlice";
import useAuth from "../../../hooks/useAuth";
import { useNavigate, Link } from 'react-router-dom';
import React, { useState } from 'react';
import { tokens } from "../../../theme";
import { CheckCircleOutline, Settings, Close } from '@mui/icons-material';
import AWSOnboardingWizard from "../s3_onboarding/OnPremiseWizard";

const initialValues = {
    name: "",
    email: ""
};

const organizationSchema = yup.object().shape({
    name: yup.string().required("Required"),
    email: yup.string()
});

const OrganizationForm = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const { username, isManager, isAdmin } = useAuth();
    const isNonMobile = useMediaQuery("(min-width:600px)");

    const [addNewOrganization, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useAddNewOrganizationMutation();

    const [organizationId, setOrganizationId] = useState(null);
    const [organizationData, setOrganizationData] = useState(null);
    const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const handleFormSubmit = async (values) => {
        const { name, email } = values;
        try {
            const response = await addNewOrganization({ name, email }).unwrap();
            setOrganizationId(response.organization.organization_id);
            setOrganizationData(response.organization);
            setShowSuccessMessage(true);
        } catch (err) {
            console.error('Failed to add new organization', err);
        }
    };

    const handleOnboardingComplete = () => {
        setShowOnboardingWizard(false);
    };

    return (
        <Box m="20px">
            {!showSuccessMessage && (
                <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={initialValues}
                    validationSchema={organizationSchema}>
                    {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
                        <form onSubmit={handleSubmit}>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" }
                                }}>
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="Member Name"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.name}
                                    name="name"
                                    error={!!touched.name && !!errors.name}
                                    helperText={touched.name && errors.name}
                                    sx={{ gridColumn: "span 2" }}
                                />
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="Email (optional)"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.email}
                                    name="email"
                                    error={!!touched.email && !!errors.email}
                                    helperText={touched.email && errors.email}
                                    sx={{ gridColumn: "span 2" }}
                                />
                            </Box>
                            <Box display="flex" justifyContent="end" mt="20px">
                                {isLoading ? (
                                    <CircularProgress />
                                ) : (
                                    <Button type="submit" variant="outlined" sx={{ color: colors.greenAccent[400], outlineColor: colors.redAccent[400] }} >
                                        Onboard New Member
                                    </Button>
                                )}
                            </Box>
                        </form>
                    )}
                </Formik>
            )}

            {showSuccessMessage && (
                <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
                    <Typography
                        variant="h4"
                        sx={{
                            color: colors.primary[100],
                            animation: 'fadeIn 1.5s ease-in-out forwards',
                            mb: 2
                        }}
                    >
                        Member Successfully Onboarded!
                    </Typography>
                </Box>
            )}

            {isSuccess && organizationId && !showOnboardingWizard && (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Button
                        variant="contained"
                        sx={{
                            background: `linear-gradient(45deg, ${colors.greenAccent[500]}, ${colors.blueAccent[500]})`,
                            color: 'white'
                        }}
                        component={Link}
                        to={`/console/organization/${organizationId}`}
                        startIcon={<CheckCircleOutline />}
                    >
                        Go to Organization
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            background: `linear-gradient(45deg, ${colors.blueAccent[500]}, ${colors.greenAccent[500]})`,
                            color: 'white'
                        }}
                        startIcon={<Settings />}
                        onClick={() => setShowOnboardingWizard(true)}
                        disabled={false}
                    >
                        Configure Organization S3 Onboarding
                    </Button>
                </Box>
            )}

            {showOnboardingWizard && organizationData && (
                <Box mt={4}>
                    <AWSOnboardingWizard organization={organizationData} />
                    <Box display="flex" justifyContent="center" mt={2} gap={2}>
                        <Button
                            variant="contained"
                            sx={{
                                background: `linear-gradient(45deg, ${colors.blueAccent[500]}, ${colors.greenAccent[500]})`,
                                color: 'white'
                            }}
                            component={Link}
                            to={`/console/organization/${organizationId}`}
                            startIcon={<CheckCircleOutline />}
                        >
                            Go to Organization
                        </Button>
                        {/*<Button
                            variant="outlined"
                            sx={{
                                color: colors.redAccent[500],
                                borderColor: colors.redAccent[500]
                            }}
                            startIcon={<Close />}
                            onClick={handleOnboardingComplete}
                        >
                            Close Onboarding
                        </Button>*/}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default OrganizationForm;
